import { useEffect, useState } from 'react';
import {
  Row,
  Col,
  Button,
  Space,
  Tooltip,
  Typography,
  Form,
  Alert,
} from 'antd';
import { isEmpty } from 'lodash';
import {
  CameraOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';

import classnames from 'classnames';

import {
  CustomNotification,
  FormAddButton,
  FormButton,
  StyledModal,
  ThumbnailItem,
  UploadFile,
  WithLoading,
} from 'common/components';

import {
  checkIsPngFile,
  checkIsTiffFile,
  convertTiffToPreview,
  getErrorMessageForImageType,
  extractImageIds,
} from 'common/components/product-add/components/utils/images';

import { useUploadProductImage } from 'common/components/product-add/components/hooks/useUploadProductImage';

import { useValidateActiveProductBase } from 'hooks/useValidateSelfLoadedProduct';

import {
  _2D_BARCODE_IMAGE_ID,
  BARCODE_FORMAT,
  BARCODE_IMAGE_ID,
  FRONT_PLANOGRAM_IMAGE_ID,
  IMAGERY_TYPES,
  PLANOGRAM_IMAGE_TYPES,
  PRODUCT_IMAGERY_LABELS_BY_LEVEL,
} from 'common/components/product-add/components/constants';

import {
  checkActiveStatus,
  checkRequiredPrimaryFieldForm,
  isUnitPackageLevel,
  mapImageryValidationErrors,
} from 'common/components/product-add/components/utils/validation';
import { sortBarcodesByFormat } from 'common/components/product-add/components/utils/barcode';

import { UPLOAD_MAX_SIZE } from 'static/Constants';

import tiff from 'assets/file-formats/tiff.png';

import './AdditionalImagerySection.less';

export const AdditionalImagerySection = (props) => {
  const {
    productImagery,
    setProductImagery,
    validateErrors: initialValidationErrors,
    memberId,
    getFormPayload,
  } = props;

  const [visibleAdditionalImagery, setVisibleAdditionalImagery] =
    useState(false);
  const [visiblePlanogram, setVisiblePlanogram] = useState(false);
  const [pendingImagery, setPendingImagery] = useState(productImagery);

  const [validateErrors, setValidateErrors] = useState(initialValidationErrors);

  useEffect(() => {
    setValidateErrors(initialValidationErrors);
  }, [initialValidationErrors]);

  useEffect(() => {
    setPendingImagery(productImagery);
  }, [productImagery]);

  const formInstance = Form.useFormInstance();

  const packageLevel = Form.useWatch('packageLevel', formInstance) || 'Unit';

  const isUnit = isUnitPackageLevel(packageLevel);

  const isActiveStatus = checkActiveStatus(Form.useWatch('status'));

  const isRequired = checkRequiredPrimaryFieldForm({
    isActiveStatus,
    isUnit,
  });

  const imageryErrors = mapImageryValidationErrors(
    validateErrors?.errors,
    packageLevel
  );

  const uploadPlanogramMutation = useUploadProductImage({
    detectBarcode: false,
    memberId,
  });

  // Get the front planogram (frontPlanogramImageId) from productImagery
  const primaryImage = pendingImagery?.frontPlanogramImageId;

  useEffect(() => {
    if (formInstance && primaryImage?.imageId) {
      formInstance.setFieldsValue({
        frontPlanogramImageId: primaryImage.imageId,
      });
    }
  }, [formInstance, primaryImage?.imageId]);

  const handlePlanogramFileChange = (fileList) => {
    const latestFile = fileList[fileList.length - 1];

    if (latestFile?.status?.includes('error')) return;

    if (latestFile?.originFileObj) {
      setPendingImagery((prev) => ({
        ...prev,
        frontPlanogramImageId: latestFile.originFileObj,
      }));

      const filteredErrors = initialValidationErrors?.errors?.filter(
        (error) =>
          error?.fieldName?.toLowerCase() !==
          FRONT_PLANOGRAM_IMAGE_ID?.toLowerCase()
      );
      setValidateErrors((prev) => ({
        ...prev,
        errors: filteredErrors,
      }));
    }
  };

  const handleValidateFrontPlanogram = (value) => {
    if (formInstance) {
      formInstance.setFieldsValue({
        frontPlanogramImageId: value,
      });
      // Explicitly trigger validation to clear the error
      formInstance.validateFields([FRONT_PLANOGRAM_IMAGE_ID]).catch(() => {});
    }
  };

  const handlePlanogramSubmit = async () => {
    const pendingFile = pendingImagery?.frontPlanogramImageId;
    if (!pendingFile || pendingFile?.imageId) return; // Skip if already uploaded or no file

    uploadPlanogramMutation.mutate(pendingFile, {
      onSuccess: async (response) => {
        if (response?.isSuccess && response?.data) {
          const isTiff = checkIsTiffFile(pendingFile);
          const imageId = response?.data?.imageId;

          const updateImagery = (previewUrl) => {
            const newImageData = {
              imageId,
              previewUrl,
              isTiff,
              status: 'draft',
            };

            setPendingImagery((prev) => ({
              ...prev,
              frontPlanogramImageId: newImageData,
            }));

            setProductImagery((prev) => ({
              ...prev,
              frontPlanogramImageId: newImageData,
            }));

            // Update form field value to trigger validation
            handleValidateFrontPlanogram(imageId);
          };

          if (isTiff) {
            try {
              // Convert TIFF to preview using UTIF
              const previewUrl = await convertTiffToPreview(pendingFile);
              updateImagery(previewUrl);
            } catch (error) {
              updateImagery(tiff);
              CustomNotification.warning(
                'TIFF preview could not be generated, showing placeholder icon'
              );
            }
          } else {
            const reader = new FileReader();
            reader.onload = (e) => {
              updateImagery(e.target.result);
            };
            reader.readAsDataURL(pendingFile);
          }

          setVisiblePlanogram(false);
          CustomNotification.success('Front Planogram uploaded successfully');
        } else {
          CustomNotification.error(
            response?.message ?? 'Failed to upload front planogram'
          );
        }
      },
      onError: (error) => {
        CustomNotification.error(
          error?.message ?? 'An error occurred while uploading'
        );
      },
    });
  };

  const handlePlanogramCancel = () => {
    setPendingImagery((prev) => ({
      ...prev,
      frontPlanogramImageId: productImagery?.frontPlanogramImageId || null,
    }));
    uploadPlanogramMutation.reset();
    setVisiblePlanogram(false);
  };
  const handleRemovePlanogram = () => {
    const isExistPlanogramError = initialValidationErrors?.errors?.some(
      (error) =>
        error?.fieldName?.toLowerCase() ===
        FRONT_PLANOGRAM_IMAGE_ID?.toLowerCase()
    );
    if (isExistPlanogramError) {
      const planogramError = initialValidationErrors?.errors?.find(
        (error) =>
          error?.fieldName?.toLowerCase() ===
          FRONT_PLANOGRAM_IMAGE_ID?.toLowerCase()
      );
      setValidateErrors((prev) => ({
        ...prev,
        errors: prev?.errors?.concat([planogramError]),
      }));
    }
    setPendingImagery((prev) => ({
      ...prev,
      frontPlanogramImageId: null,
    }));
    setProductImagery((prev) => ({
      ...prev,
      frontPlanogramImageId: null,
    }));
    handleValidateFrontPlanogram();
  };
  return (
    <>
      <Form.Item
        name='frontPlanogramImageId'
        rules={
          isRequired
            ? [
                {
                  validator: (_, value) => {
                    // Check if the value has imageId
                    if (!value) {
                      return Promise.reject(
                        new Error('Front Planogram is required')
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]
            : []
        }
      >
        <Row>
          <Col flex='100px'>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <ThumbnailItem
                hideCheckbox
                style={{ marginLeft: 0 }}
                imageThumbnail={primaryImage?.previewUrl}
                labelThumbnail='Front Planogram'
              />
              {primaryImage && (
                <div className='front-planogram-wrapper'>
                  <span className='front-planogram-close'>
                    <CloseCircleOutlined
                      style={{
                        fontSize: 20,
                        color: '#ff4d4f',
                        cursor: 'pointer',
                        backgroundColor: '#fff',
                        borderRadius: '50%',
                        display: 'block',
                        pointerEvents: 'auto',
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRemovePlanogram();
                      }}
                    />
                  </span>
                </div>
              )}
            </div>
          </Col>

          <Col flex='auto'>
            <Space size={12}>
              {isRequired && (
                <Typography.Text type='danger' strong style={{ fontSize: 14 }}>
                  *
                </Typography.Text>
              )}
              <Button
                onClick={() => setVisiblePlanogram(true)}
                loading={uploadPlanogramMutation.isLoading}
              >
                Load Front Planogram
              </Button>
              <FormAddButton
                text='More'
                onClick={() => setVisibleAdditionalImagery(true)}
              />
            </Space>
          </Col>
        </Row>
      </Form.Item>

      {visibleAdditionalImagery && (
        <AdditionalImageryModal
          visible={visibleAdditionalImagery}
          productImagery={productImagery}
          imageryErrors={imageryErrors}
          setProductImagery={setProductImagery}
          setPendingImagery={setPendingImagery}
          pendingImagery={pendingImagery}
          onCancel={() => setVisibleAdditionalImagery(false)}
          onClose={() => setVisibleAdditionalImagery(false)}
          memberId={memberId}
          onUpdateValidationErrors={(errors) => setValidateErrors(errors)}
          getFormPayload={getFormPayload}
        />
      )}

      {visiblePlanogram && (
        <StyledModal
          visible={visiblePlanogram}
          maskClosable={false}
          onCancel={handlePlanogramCancel}
          onOk={handlePlanogramSubmit}
          closable={!uploadPlanogramMutation.isLoading}
          title='Upload Front Planogram'
          okText='Upload'
          cancelText='Cancel'
          okButtonProps={{
            disabled:
              !pendingImagery?.frontPlanogramImageId ||
              pendingImagery?.frontPlanogramImageId?.imageId,
            loading: uploadPlanogramMutation.isLoading,
          }}
        >
          <ImageUpload
            onFileChange={handlePlanogramFileChange}
            customErrorMessage={getErrorMessageForImageType('c1N1ImageId')}
          />
        </StyledModal>
      )}
    </>
  );
};

const AdditionalImageryModal = (props) => {
  const {
    productImagery,
    setProductImagery,
    pendingImagery,
    setPendingImagery,
    imageryErrors,
    memberId,
    onClose,
    onUpdateValidationErrors,
    getFormPayload,
    ...modalProps
  } = props;

  const [loadingStates, setLoadingStates] = useState({});

  const formInstance = Form.useFormInstance();

  const packageLevel = Form.useWatch('packageLevel', formInstance) || 'Unit';

  const validationMutation = useValidateActiveProductBase();

  useEffect(() => {
    if (modalProps?.visible) {
      setPendingImagery(productImagery);
    }
  }, [modalProps?.visible, productImagery, setPendingImagery]);

  const handleUploadImage = (positionKey) => {
    setLoadingStates((prev) => ({
      ...prev,
      [positionKey]: true,
    }));
  };

  const handleUploadSuccess = async (response, uploadedFile, positionKey) => {
    if (response?.isSuccess && response?.data) {
      const isTiff = checkIsTiffFile(uploadedFile);
      const barcodes = response?.data?.detectedBarcodes || null;

      if (isTiff) {
        try {
          const previewUrl = await convertTiffToPreview(uploadedFile);

          setPendingImagery((prev) => ({
            ...prev,
            [positionKey]: {
              imageId: response.data.imageId,
              previewUrl,
              isTiff: true,
              barcodes,
              status: 'draft',
            },
          }));
        } catch (error) {
          setPendingImagery((prev) => ({
            ...prev,
            [positionKey]: {
              imageId: response.data.imageId,
              previewUrl: tiff,
              isTiff: true,
              barcodes,
              status: 'draft',
            },
          }));
          CustomNotification.warning(
            'TIFF preview could not be generated, showing placeholder icon'
          );
        }

        setLoadingStates((prev) => ({
          ...prev,
          [positionKey]: false,
        }));
      } else {
        const reader = new FileReader();
        reader.onload = (e) => {
          setPendingImagery((prev) => ({
            ...prev,
            [positionKey]: {
              imageId: response.data.imageId,
              previewUrl: e.target.result,
              isTiff: false,
              barcodes,
              status: 'draft',
            },
          }));
          setLoadingStates((prev) => ({
            ...prev,
            [positionKey]: false,
          }));
        };
        reader.readAsDataURL(uploadedFile);
      }
    } else {
      setLoadingStates((prev) => ({
        ...prev,
        [positionKey]: false,
      }));
      CustomNotification.error(response?.message ?? 'Failed to upload image');
    }
  };

  const handleUploadError = (error, positionKey) => {
    setLoadingStates((prev) => ({
      ...prev,
      [positionKey]: false,
    }));
    CustomNotification.error(
      error?.message ?? 'An error occurred while uploading'
    );
  };

  const handleRemoveImage = (positionKey) => {
    setPendingImagery((prev) => ({
      ...prev,
      [positionKey]: null,
    }));
  };

  const handleSubmit = () => {
    setProductImagery(pendingImagery);

    if (formInstance) {
      if (pendingImagery?.frontPlanogramImageId?.imageId) {
        formInstance.setFieldsValue({
          [FRONT_PLANOGRAM_IMAGE_ID]:
            pendingImagery.frontPlanogramImageId.imageId,
        });
        formInstance.validateFields([FRONT_PLANOGRAM_IMAGE_ID]).catch(() => {});
      } else {
        formInstance.setFieldsValue({
          [FRONT_PLANOGRAM_IMAGE_ID]: null,
        });
        formInstance.validateFields([FRONT_PLANOGRAM_IMAGE_ID]).catch(() => {});
      }
    }

    onClose();
  };

  const handleValidateImages = async () => {
    let payload = getFormPayload();
    const mappingProductImagery = extractImageIds(pendingImagery);

    payload = {
      ...payload,
      ...mappingProductImagery,
    };

    const response = await validationMutation.mutateAsync(payload);
    if (response?.isSuccess) {
      onUpdateValidationErrors(response?.data?.activeResult || {});
    }
  };

  const handleCancel = () => {
    setPendingImagery(productImagery);
    modalProps.onCancel?.();
  };

  const hasLoadingImages = Object.values(loadingStates).some(
    (loading) => loading === true
  );

  return (
    <StyledModal
      title={
        <Typography.Text>
          Drag and drop your images for{' '}
          <Typography.Link>{packageLevel} </Typography.Link>
          into the appropriate location
        </Typography.Text>
      }
      width={900}
      maskClosable={false}
      wrapClassName='create-product-form__additional-imagery-modal'
      centered
      bodyStyle={{
        paddingTop: 4,
      }}
      footer={
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            onClick={handleValidateImages}
            disabled={hasLoadingImages}
            loading={validationMutation.isLoading}
          >
            Validate Image
          </Button>
          <Button
            type='primary'
            onClick={handleSubmit}
            disabled={hasLoadingImages}
          >
            Submit
          </Button>
        </Space>
      }
      {...modalProps}
    >
      <Row gutter={[12, 12]} style={{ marginTop: 8 }}>
        {Object.keys(pendingImagery).map((positionKey) => {
          return (
            <Col span={6} key={positionKey}>
              <AdditionalImageryItem
                label={
                  PRODUCT_IMAGERY_LABELS_BY_LEVEL?.[`${packageLevel}`][
                    positionKey
                  ]
                }
                error={imageryErrors?.[positionKey] ?? null}
                positionKey={positionKey}
                productImagery={pendingImagery}
                loadingStates={loadingStates}
                onUploadImage={handleUploadImage}
                onUploadSuccess={handleUploadSuccess}
                onUploadError={handleUploadError}
                onRemoveImage={handleRemoveImage}
                memberId={memberId}
              />
            </Col>
          );
        })}
      </Row>
    </StyledModal>
  );
};

const AdditionalImageryItem = ({
  label,
  positionKey,
  error,
  productImagery,
  loadingStates,
  onUploadImage,
  onUploadSuccess,
  onUploadError,
  onRemoveImage,
  memberId,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  const imageData = productImagery?.[positionKey];
  const isLoading = loadingStates[positionKey];
  const hasImage =
    !isEmpty(imageData?.previewUrl) || !isEmpty(imageData?.imageId);

  // Enable barcode detection for l3ImageId and l6ImageId
  const shouldDetectBarcode =
    positionKey === BARCODE_IMAGE_ID || positionKey === _2D_BARCODE_IMAGE_ID;

  const uploadMutation = useUploadProductImage({
    detectBarcode: shouldDetectBarcode,
    memberId,
  });

  const performUpload = (file) => {
    onUploadImage(positionKey);

    uploadMutation.mutate(file, {
      onSuccess: (response) => {
        if (response?.isSuccess) {
          onUploadSuccess(response, file, positionKey);
        } else {
          onUploadError(
            { message: response?.message || 'Upload failed' },
            positionKey
          );
        }
      },
      onError: (error) => {
        onUploadError(error, positionKey);
      },
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    if (isLoading) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];

      if (checkIsTiffFile(file) || checkIsPngFile(file)) {
        performUpload(file);
      } else {
        CustomNotification.error(getErrorMessageForImageType(positionKey));
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!isLoading) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileChange = (fileList) => {
    const latestFile = fileList[fileList.length - 1];

    if (latestFile?.status?.includes('error')) {
      return;
    }

    if (fileList && fileList.length > 0) {
      const file = latestFile?.originFileObj || latestFile;
      setPendingFile(file);
    }
  };

  const handleConfirmUpload = () => {
    if (pendingFile) {
      performUpload(pendingFile);
      setPendingFile(null);
      setIsEditMode(false);
    }
  };

  const handleCancelUpload = () => {
    setPendingFile(null);
    setIsEditMode(false);
  };

  return (
    <>
      <div
        className={`additional-imagery__placeholder-container ${
          isDragOver ? 'drag-over' : ''
        } ${hasImage ? 'has-image' : ''} ${isLoading ? 'is-loading' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => {
          if (!isLoading) {
            setIsEditMode(true);
          }
        }}
      >
        {isLoading ? (
          <div className='additional-imagery__loading-container'>
            <WithLoading>
              <ThumbnailItem
                className='additional-imagery__thumbnail'
                hideCheckbox
                labelThumbnail={label}
                imageThumbnail={null}
                style={{ opacity: 0.5 }}
              />
            </WithLoading>
          </div>
        ) : hasImage ? (
          <ImageModifier
            onToggleEditImage={() => setIsEditMode(true)}
            onRemoveImage={() => {
              onRemoveImage(positionKey);
              setIsEditMode(false);
            }}
          >
            <ThumbnailItem
              className={classnames({
                'additional-imagery__thumbnail': true,
                'additional-imagery__thumbnail--isTiff': Boolean(
                  imageData?.isTiff
                ),
              })}
              hideCheckbox
              labelThumbnail={label}
              imageThumbnail={imageData?.previewUrl || null}
            />
            {shouldDetectBarcode && (
              <BarcodeDetectionStatus
                barcodes={imageData?.barcodes}
                isDraftImage={imageData?.status?.toLowerCase() === 'draft'}
              />
            )}
          </ImageModifier>
        ) : (
          <ThumbnailItem
            className={classnames({
              'additional-imagery__thumbnail': true,
            })}
            hideCheckbox
            labelThumbnail={label}
            imageThumbnail={null}
          />
        )}
      </div>

      {error && (
        <div style={{ padding: 4 }}>
          <ImageError
            errorMessage={error?.errorMessage}
            style={{ padding: '4px 8px' }}
          />
        </div>
      )}

      {isEditMode && (
        <StyledModal
          visible={isEditMode}
          maskClosable={false}
          onCancel={handleCancelUpload}
          onOk={handleConfirmUpload}
          closable={true}
          title={hasImage ? 'Update Image' : 'Upload Image'}
          okText='Upload'
          cancelText='Cancel'
          okButtonProps={{ disabled: !pendingFile }}
        >
          <ImageUpload
            onFileChange={handleFileChange}
            supportTypes={IMAGERY_TYPES?.[positionKey]}
            customErrorMessage={getErrorMessageForImageType(positionKey)}
          />
        </StyledModal>
      )}
    </>
  );
};

export const ImageError = ({
  errorMessage,
  isWarning = false,
  style = {},
  ...props
}) => {
  if (!errorMessage) return null;

  return (
    <Alert
      message={<Typography.Text strong>{errorMessage}</Typography.Text>}
      type={isWarning ? 'warning' : 'error'}
      showIcon
      style={{ marginTop: 8, fontSize: 12, padding: 4, ...style }}
      {...props}
    />
  );
};
export const BarcodeDetectionStatus = ({ barcodes, isDraftImage }) => {
  const formInstance = Form.useFormInstance();

  // Get current form values for comparison
  const gtin = Form.useWatch('gtin', formInstance);
  const upc12 = Form.useWatch('upc12', formInstance);
  const upc8 = Form.useWatch('upc8', formInstance);

  // Map form field names to barcode format types
  const formValueMap = {
    [BARCODE_FORMAT.GTIN]: gtin,
    [BARCODE_FORMAT.UPC_E]: upc8,
    [BARCODE_FORMAT.UPC_A]: upc12,
  };

  const barcodeNeedToCheck = Object.keys(BARCODE_FORMAT).map((code) =>
    code.toLowerCase()
  );

  // If no barcodes detected
  if (!barcodes || barcodes.length === 0) {
    if (isDraftImage) {
      return (
        <div style={{ marginTop: 8, padding: 8, backgroundColor: '#fff9e6' }}>
          <Space size={4}>
            <WarningOutlined style={{ color: '#faad14', fontSize: 14 }} />
            <Typography.Text style={{ fontSize: 12, color: '#faad14' }}>
              No barcode detected
            </Typography.Text>
          </Space>
        </div>
      );
    }
    return null;
  }

  // Sort barcodes: non-priority first (original order), then priority (sorted)
  const sortedBarcodes = sortBarcodesByFormat(barcodes) || barcodes;

  // Check if any barcode has errors or mismatches
  const hasErrors = sortedBarcodes
    .filter(
      (barcode) =>
        !barcodeNeedToCheck.includes(barcode.barcodeFormat?.toLowerCase())
    )
    .some((barcode) => {
      if (barcode.error) return true;

      const fieldName =
        BARCODE_FORMAT[barcode.barcodeFormat] || barcode.barcodeFormat;
      const formValue = formValueMap[fieldName];

      // Check if there's a mismatch between detected and form value
      if (formValue !== barcode.barcodeText) {
        return true;
      }

      return false;
    });

  return (
    <div
      style={{
        marginTop: 8,
        padding: 8,
        backgroundColor: hasErrors ? '#fff9e6' : '#f6ffed',
      }}
    >
      <Space direction='vertical' size={4} style={{ width: '100%' }}>
        <Space size={4}>
          {hasErrors ? (
            <WarningOutlined style={{ color: '#faad14', fontSize: 14 }} />
          ) : (
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 14 }} />
          )}
          <Typography.Text
            style={{
              fontSize: 12,
              color: hasErrors ? '#faad14' : '#52c41a',
            }}
            strong
          >
            Barcode detected
          </Typography.Text>
        </Space>

        {sortedBarcodes.map((barcode, index) => {
          const barcodeFormat = barcode.barcodeFormat;
          const isValidatingCode = barcodeNeedToCheck.includes(
            barcodeFormat?.toLowerCase()
          );

          const fieldName =
            BARCODE_FORMAT[barcode.barcodeFormat] || barcode.barcodeFormat;
          const formValue = formValueMap[fieldName] || 'N/A';
          const hasMatch =
            !isValidatingCode || formValue === barcode.barcodeText; // ignore some fields for matching
          const hasMismatch =
            isValidatingCode && formValue !== barcode.barcodeText;

          return (
            <div key={index}>
              {barcode.error ? (
                // Show API error
                <Space size={4}>
                  <CloseCircleOutlined
                    style={{ color: '#ff4d4f', fontSize: 12 }}
                  />
                  <Typography.Text
                    style={{ fontSize: 12, color: '#ff4d4f' }}
                    type='danger'
                  >
                    {fieldName?.toUpperCase()}: {barcode.error}
                  </Typography.Text>
                </Space>
              ) : hasMismatch ? (
                <Space direction='vertical' size={2}>
                  <Space size={4}>
                    <WarningOutlined
                      style={{ color: '#faad14', fontSize: 12 }}
                    />
                    <Typography.Text style={{ fontSize: 12, color: '#faad14' }}>
                      <strong>
                        {fieldName?.toUpperCase() || barcode.barcodeFormat}:
                      </strong>{' '}
                    </Typography.Text>
                  </Space>
                  <div>
                    <Typography.Text type='secondary'>
                      Your value: <strong>{formValue}</strong>
                    </Typography.Text>
                    <br />
                    <Typography.Text type='secondary'>
                      Detected: <strong>{barcode.barcodeText}</strong>
                    </Typography.Text>
                  </div>
                </Space>
              ) : hasMatch ? (
                <Space size={4}>
                  <CheckCircleOutlined
                    style={{ color: '#52c41a', fontSize: 12 }}
                  />
                  <Typography.Text style={{ fontSize: 12, color: '#52c41a' }}>
                    <strong>
                      {fieldName?.toUpperCase() || barcode.barcodeFormat}:
                    </strong>{' '}
                    {barcode.barcodeText}
                  </Typography.Text>
                </Space>
              ) : (
                // Show detected value (no form value to compare or non-priority format)
                <Typography.Text style={{ fontSize: 12, color: '#595959' }}>
                  <strong>
                    {fieldName?.toUpperCase() || barcode.barcodeFormat}:
                  </strong>{' '}
                  {barcode.barcodeText}
                </Typography.Text>
              )}
            </div>
          );
        })}
      </Space>
    </div>
  );
};

export const ImageModifier = ({
  children,
  onToggleEditImage,
  onRemoveImage,
  className,
  ...otherProps
}) => {
  return (
    <div
      className={classnames({
        'additional-imagery__modifier': true,
        [className]: !!className,
      })}
      {...otherProps}
    >
      <div className='additional-imagery__modifier-icon-wrapper'>
        <Space direction='vertical' size={2}>
          <Tooltip title='Change image' placement='right'>
            <FormButton
              text={null}
              type='secondary'
              icon={
                <CameraOutlined
                  className='additional-imagery__modifier-icon'
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleEditImage();
                  }}
                />
              }
            />
          </Tooltip>
          <Tooltip title='Delete image' placement='right'>
            <FormButton
              text={null}
              type='secondary'
              icon={
                <CloseCircleOutlined
                  className='additional-imagery__delete-icon'
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveImage();
                  }}
                />
              }
            />
          </Tooltip>
        </Space>
      </div>
      {children}
    </div>
  );
};

export const ImageUpload = ({
  onFileChange,
  supportTypes,
  customErrorMessage,
  ...props
}) => {
  return (
    <UploadFile
      manualUpload
      allowDeleteFile
      onFileChange={onFileChange}
      showMultiple={false}
      supportTypes={supportTypes ?? PLANOGRAM_IMAGE_TYPES}
      showSubmitBtn={false}
      maxSize={UPLOAD_MAX_SIZE.GENERAL}
      showPreview={true}
      customErrorMessage={{
        'error.fileType': customErrorMessage,
      }}
      {...props}
    />
  );
};
